import AppComponent from '../base/app-component.ts';

/**
 * wc-edit-lavoratore — Inserimento e modifica lavoratore.
 *
 * Attributi:
 *   lavoratore-id  (opzionale) ID lavoratore da modificare; assente = inserimento
 *
 * Emette:
 *   lavoratore-salvato  { detail: { lavoratore, isNew } }
 */
export class EditLavoratore extends AppComponent {
  static override componentName = 'wc-edit-lavoratore';

  protected override get templateId() { return 'edit-lavoratore'; }

  private editLavoratoreId = 0;

  protected override afterRender(): void {
    const lavoratoreId = this.attributeNumber('lavoratore-id');
    if (lavoratoreId) {
      this.editLavoratoreId = lavoratoreId;
      this.loadLavoratore(lavoratoreId);
      this.setTitleModifica();
    }
    this.setupForm();
  }

  /** Reset completo per riapertura in modalità "nuovo" */
  reset(): void {
    this.editLavoratoreId = 0;
    this.clearForm();
    this.setTitleNuovo();
  }

  /** Carica un lavoratore esistente per la modifica */
  edit(id: number): void {
    this.editLavoratoreId = id;
    this.clearForm();
    this.loadLavoratore(id);
    this.setTitleModifica();
  }

  private clearForm(): void {
    for (const el of this.$$('input')) {
      const input = el as HTMLInputElement;
      input.value = '';
      input.readOnly = false;
    }
    this.resetCfDati();
    const cfDati = this.$('[data-cf-dati]') as HTMLElement | null;
    if (cfDati) cfDati.classList.add('d-none');
    this.nascondiValidationError();
  }

  // --- Setup ---

  private setupForm(): void {
    const cfInput = this.$('[data-field="cf"]') as HTMLInputElement | null;
    if (cfInput) {
      this.listen(cfInput, 'input', () => {
        this.onCfInput(cfInput.value.trim().toUpperCase());
      });
    }

    const form = this.$('[data-form="edit-lavoratore"]') as HTMLFormElement | null;
    if (form) {
      this.listen(form, 'submit', (e: Event) => {
        e.preventDefault();
        this.handleSubmit();
      });
    }
  }

  // --- Decodifica CF ---

  private async onCfInput(cf: string): Promise<void> {
    const container = this.$('[data-cf-dati]') as HTMLElement | null;
    if (!container) return;

    if (cf.length !== 16) {
      container.classList.add('d-none');
      this.resetCfDati();
      if (this.editLavoratoreId && !this.attributeNumber('lavoratore-id')) {
        this.editLavoratoreId = 0;
      }
      return;
    }

    const result = await this.api.lavoratori.decodificaCf(cf);
    if (!result.ok) {
      container.classList.add('d-none');
      this.resetCfDati();
      this.editLavoratoreId = 0;
      return;
    }

    const d = result.data;
    this.setCfDato('[data-cf-sesso]', d.sesso);
    this.setCfDato('[data-cf-data-nascita]', this.formatDate(d.dataNascita));
    this.setCfDato('[data-cf-luogo-nascita]', d.luogoNascita ?? '');
    this.setCfDato('[data-cf-provincia-nascita]', d.provinciaNascita ?? '');
    container.classList.remove('d-none');

    if (d.lavoratore) {
      const lav = d.lavoratore;
      const conferma = await Weext.confirm(
        `Lavoratore già in archivio: ${lav.cognome} ${lav.nome}. Vuoi modificarne i dati?`,
        { title: 'Lavoratore esistente', confirmText: 'Modifica', cancelText: 'Annulla' }
      );
      if (conferma) {
        this.editLavoratoreId = lav.id;
        this.populateForm(lav);
        const cfField = this.$('[data-field="cf"]') as HTMLInputElement | null;
        if (cfField) cfField.readOnly = true;
        this.setTitleModifica();
      } else {
        this.editLavoratoreId = 0;
        const cfField = this.$('[data-field="cf"]') as HTMLInputElement | null;
        if (cfField) cfField.value = '';
        container.classList.add('d-none');
        this.resetCfDati();
      }
    } else {
      this.editLavoratoreId = 0;
    }
  }

  // --- Caricamento dati per edit ---

  private async loadLavoratore(id: number): Promise<void> {
    const result = await this.api.lavoratori.dettaglio(id);
    if (!result.ok) {
      this.mostraValidationError(result.error);
      return;
    }
    this.populateForm(result.data);
    const cfInput = this.$('[data-field="cf"]') as HTMLInputElement | null;
    if (cfInput) {
      cfInput.value = result.data.codiceFiscale ?? '';
      cfInput.readOnly = true;
    }
  }

  // --- Submit ---

  private async handleSubmit(): Promise<void> {
    const val = (sel: string) => (this.$(`[data-field="${sel}"]`) as HTMLInputElement | null)?.value.trim() ?? '';
    const cf = val('cf').toUpperCase();
    const cognome = val('cognome');
    const nome = val('nome');

    if (!cf || !cognome || !nome) {
      this.mostraValidationError('Codice fiscale, cognome e nome sono obbligatori');
      return;
    }
    if (cf.length !== 16) {
      this.mostraValidationError('Il codice fiscale deve essere di 16 caratteri');
      return;
    }

    const dati: Record<string, string> = { codiceFiscale: cf, cognome, nome };
    for (const campo of ['via', 'cap', 'comune', 'provincia', 'telefono', 'cellulare', 'email'] as const) {
      const v = val(campo);
      if (v) dati[campo] = v;
    }

    if (this.editLavoratoreId > 0) {
      const result = await this.api.lavoratori.aggiorna(this.editLavoratoreId, dati);
      if (!result.ok) {
        this.mostraValidationError(result.error);
        return;
      }
      Weext.toast('Lavoratore aggiornato', { type: 'success' });
      this.dispatch('lavoratore-salvato', { lavoratore: result.data, isNew: false });
    } else {
      const result = await this.api.lavoratori.creaORecupera(dati);
      if (!result.ok) {
        this.mostraValidationError(result.error);
        return;
      }
      if (result.duplicato) {
        Weext.toast('Lavoratore già presente, selezionato automaticamente', { type: 'info' });
      } else {
        Weext.toast('Lavoratore inserito', { type: 'success' });
      }
      this.dispatch('lavoratore-salvato', { lavoratore: result.data, isNew: !result.duplicato });
    }
  }

  // --- Helpers ---

  private populateForm(lav: Record<string, unknown>): void {
    const campi: Record<string, string> = {
      cognome: 'cognome', nome: 'nome',
      via: 'via', cap: 'cap', comune: 'comune', provincia: 'provincia',
      telefono: 'telefono', cellulare: 'cellulare', email: 'email',
    };
    for (const [key, field] of Object.entries(campi)) {
      const input = this.$(`[data-field="${field}"]`) as HTMLInputElement | null;
      if (input) input.value = (lav[key] as string) ?? '';
    }
  }

  private setCfDato(sel: string, value: string): void {
    const el = this.$(sel) as HTMLInputElement | null;
    if (el) el.value = value;
  }

  private resetCfDati(): void {
    for (const el of this.$$('[data-cf-sesso],[data-cf-data-nascita],[data-cf-luogo-nascita],[data-cf-provincia-nascita]')) {
      (el as HTMLInputElement).value = '';
    }
  }

  private formatDate(iso: string): string {
    const [y, m, d] = iso.split('-');
    return (y && m && d) ? `${d}/${m}/${y}` : iso;
  }

  private mostraValidationError(messaggio: string): void {
    const el = this.$('[data-validation-error]');
    if (!el) return;
    el.textContent = messaggio;
    el.classList.remove('d-none');
  }

  private nascondiValidationError(): void {
    const el = this.$('[data-validation-error]');
    if (!el) return;
    el.textContent = '';
    el.classList.add('d-none');
  }

  private setTitleNuovo(): void {
    const title = this.$('[data-modal-title]');
    if (title) title.innerHTML = '<i class="bi bi-person-plus me-2"></i>Nuovo lavoratore';
  }

  private setTitleModifica(): void {
    const title = this.$('[data-modal-title]');
    if (title) title.innerHTML = '<i class="bi bi-pencil me-2"></i>Modifica lavoratore';
  }
}
